import {
  BOT_SEQUENCE_DEFAULT_BODY_LOCALE,
  BOT_SEQUENCE_STEP_LIMITS,
  BotSequenceConfigStepDelivery,
  BotSequenceDeliveryChannel,
  BotSequenceType,
  Channel,
  defaultCopyFor
} from "@swivl-mono/types";
import { Switch, TextArea } from "@swivl-mono/ui";

import { LocaleBodiesEditor } from "./LocaleBodiesEditor";

interface DeliveryEditorProps {
  channel: BotSequenceDeliveryChannel;
  delivery: BotSequenceConfigStepDelivery | undefined;
  /** For the code-baked defaults a field is prefilled with when switched on (decisions 51–53). */
  sequenceType: BotSequenceType;
  offsetDays: number;
  disabled: boolean;
  onChange: (next: BotSequenceConfigStepDelivery | undefined) => void;
}

interface ContentModeRadioProps {
  mode: "generated" | "verbatim";
  disabled: boolean;
  onChange: (mode: "generated" | "verbatim") => void;
}

function ContentModeRadio({ mode, disabled, onChange }: ContentModeRadioProps): React.ReactElement {
  return (
    <div className="body-sm flex items-center gap-6 text-grey-900">
      <label className="flex cursor-pointer items-center gap-2">
        <input type="radio" checked={mode === "verbatim"} disabled={disabled} onChange={() => onChange("verbatim")} className="accent-orange-500" />
        Exact wording
      </label>
      <label className="flex cursor-pointer items-center gap-2">
        <input type="radio" checked={mode === "generated"} disabled={disabled} onChange={() => onChange("generated")} className="accent-orange-500" />
        Written by the assistant
      </label>
    </div>
  );
}

/**
 * One channel of a day: on/off, written by the assistant (optional guidance) or exact wording
 * per language; SMS adds the multi-segment allowance, Voice adds the voicemail setting
 * (ENG-3216 decisions 13/35).
 */
export function DeliveryEditor({ channel, delivery, sequenceType, offsetDays, disabled, onChange }: DeliveryEditorProps): React.ReactElement {
  const isVoice = channel === Channel.VOICE;
  const title = isVoice ? "Voice call" : "SMS";
  const on = delivery !== undefined;
  const defaults = defaultCopyFor(sequenceType, offsetDays);

  // Switched on: a text message starts as the default exact wording (decision 51); a call
  // starts written by the assistant with the default guidance.
  const handleToggle = (next: boolean): void => {
    if (!next) {
      onChange(undefined);
      return;
    }
    onChange(
      isVoice
        ? { channel, content: { mode: "generated", guidance: defaults.guidance } }
        : { channel, content: { mode: "verbatim", bodies: { [BOT_SEQUENCE_DEFAULT_BODY_LOCALE]: defaults.sms } } }
    );
  };

  // Switching mode never leaves an empty field behind: the default wording or guidance fills it.
  const handleModeChange = (mode: "generated" | "verbatim"): void => {
    if (!delivery) return;
    const existingGuidance = delivery.content.mode === "generated" ? delivery.content.guidance : undefined;
    const existingBodies = delivery.content.mode === "verbatim" ? delivery.content.bodies : undefined;
    onChange({
      ...delivery,
      content:
        mode === "generated"
          ? { mode: "generated", guidance: existingGuidance?.trim() ? existingGuidance : defaults.guidance }
          : {
              mode: "verbatim",
              bodies: existingBodies?.[BOT_SEQUENCE_DEFAULT_BODY_LOCALE]?.trim()
                ? existingBodies
                : { ...existingBodies, [BOT_SEQUENCE_DEFAULT_BODY_LOCALE]: isVoice ? defaults.voicemail : defaults.sms }
            }
    });
  };

  const handleGuidanceChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    if (!delivery) return;
    onChange({ ...delivery, content: { mode: "generated", guidance: event.target.value } });
  };

  const handleBodiesChange = (bodies: Record<string, string>): void => {
    if (!delivery) return;
    onChange({ ...delivery, content: { mode: "verbatim", bodies } });
  };

  const handleAllowSplit = (allowSplit: boolean): void => {
    if (!delivery) return;
    onChange({ ...delivery, allowSplit });
  };

  const handleVoicemailToggle = (enabled: boolean): void => {
    if (!delivery) return;
    const bodies = delivery.voicemail?.bodies;
    const prefilled = bodies?.[BOT_SEQUENCE_DEFAULT_BODY_LOCALE]?.trim()
      ? bodies
      : { ...bodies, [BOT_SEQUENCE_DEFAULT_BODY_LOCALE]: defaults.voicemail };
    onChange({ ...delivery, voicemail: { enabled, bodies: prefilled } });
  };

  const handleVoicemailBodies = (bodies: Record<string, string>): void => {
    if (!delivery) return;
    onChange({ ...delivery, voicemail: { enabled: delivery.voicemail?.enabled ?? true, bodies } });
  };

  return (
    <section className="rounded-lg border border-grey-200 bg-white">
      <div className="flex items-center justify-between gap-4 px-4 py-3">
        <div className="body-sm-alt text-grey-900">{title}</div>
        <Switch on={on} onSet={handleToggle} isDisabled={disabled} activeColor="orange" />
      </div>
      {delivery && (
        <div className="flex flex-col gap-3 border-t border-grey-100 px-4 py-3">
          <ContentModeRadio mode={delivery.content.mode} disabled={disabled} onChange={handleModeChange} />
          {delivery.content.mode === "generated" ? (
            <TextArea
              value={delivery.content.guidance ?? ""}
              onChange={handleGuidanceChange}
              disabled={disabled}
              rows={3}
              maxLength={BOT_SEQUENCE_STEP_LIMITS.guidance}
              placeholder="Anything the assistant should say or avoid"
              helperText={
                isVoice
                  ? "The call opens with the location's disclaimer, then this, then the assistant handles the conversation live."
                  : "The assistant writes the message from the tenant's current data plus this guidance."
              }
            />
          ) : (
            <LocaleBodiesEditor
              bodies={delivery.content.bodies}
              onChange={handleBodiesChange}
              disabled={disabled}
              placeholder="Type {{ to insert a variable"
              maxLength={BOT_SEQUENCE_STEP_LIMITS.verbatimBody}
              helperText="Sent word for word. Tenants whose language has no body get the English body. A missing variable value fails this send and is recorded."
            />
          )}
          {!isVoice && delivery.content.mode === "verbatim" && (
            <label className="body-xs flex items-center gap-2 text-grey-700">
              <input
                type="checkbox"
                checked={delivery.allowSplit === true}
                disabled={disabled}
                onChange={(event) => handleAllowSplit(event.target.checked)}
                className="accent-orange-500"
              />
              Allow more than one text segment
            </label>
          )}
          {isVoice && (
            <div className="flex flex-col gap-3 border-t border-grey-100 pt-3">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="body-sm-alt text-grey-900">Leave a voicemail if nobody answers</div>
                  <div className="body-xs text-grey-600">Spoken after the beep, word for word. Off = the bot hangs up when it reaches a machine.</div>
                </div>
                <Switch on={delivery.voicemail?.enabled === true} onSet={handleVoicemailToggle} isDisabled={disabled} activeColor="orange" />
              </div>
              {delivery.voicemail?.enabled && (
                <LocaleBodiesEditor
                  bodies={delivery.voicemail.bodies}
                  onChange={handleVoicemailBodies}
                  disabled={disabled}
                  placeholder="Type {{ to insert a variable"
                  maxLength={BOT_SEQUENCE_STEP_LIMITS.verbatimBody}
                  helperText="Exact wording only."
                />
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
